import { z } from 'zod'

export const PixelProviderSchema = z.enum(['facebook', 'tiktok', 'google_ads', 'utmify'])
export type PixelProvider = z.infer<typeof PixelProviderSchema>

export const GoogleAdsConversionEventSchema = z.enum(['purchase', 'begin_checkout'])
export type GoogleAdsConversionEvent = z.infer<typeof GoogleAdsConversionEventSchema>

export const PIXEL_SECRET_MASK = '********'

export const FACEBOOK_PIXEL_ID_PATTERN = /^\d{5,}$/
export const TIKTOK_PIXEL_ID_PATTERN = /^[A-Za-z0-9]{5,}$/
export const GOOGLE_ADS_CONVERSION_ID_PATTERN = /^AW-\d+$/

export const FacebookPixelFieldsSchema = z.object({
  provider: z.literal('facebook'),
  pixelId: z.string().catch(''),
  accessToken: z.string().catch(''),
})

export const TiktokPixelFieldsSchema = z.object({
  provider: z.literal('tiktok'),
  pixelId: z.string().catch(''),
  accessToken: z.string().catch(''),
})

export const GoogleAdsPixelFieldsSchema = z.object({
  provider: z.literal('google_ads'),
  pixelId: z.string().catch(''),
  conversionLabel: z.string().catch(''),
  conversionEvent: GoogleAdsConversionEventSchema.catch('purchase'),
})

export const UtmifyPixelFieldsSchema = z.object({
  provider: z.literal('utmify'),
  accessToken: z.string().catch(''),
})

export type PixelConfigFields =
  | z.infer<typeof FacebookPixelFieldsSchema>
  | z.infer<typeof TiktokPixelFieldsSchema>
  | z.infer<typeof GoogleAdsPixelFieldsSchema>
  | z.infer<typeof UtmifyPixelFieldsSchema>

export function isMaskedPixelSecret(value: string) {
  const trimmed = value.trim()
  return trimmed.length > 0 && /^\*+$/.test(trimmed)
}

export function pixelSecretPresent(value: string) {
  return value.trim().length > 0
}

export function pixelIdFormatError(pixel: PixelConfigFields): string | undefined {
  if (pixel.provider === 'utmify') return undefined
  const value = pixel.pixelId.trim()
  if (!value) return undefined
  if (pixel.provider === 'facebook') {
    return FACEBOOK_PIXEL_ID_PATTERN.test(value) ? undefined : 'Use apenas dígitos (mínimo 5).'
  }
  if (pixel.provider === 'tiktok') {
    return TIKTOK_PIXEL_ID_PATTERN.test(value)
      ? undefined
      : 'Use letras e números (mínimo 5, sem espaços).'
  }
  return GOOGLE_ADS_CONVERSION_ID_PATTERN.test(value) ? undefined : 'Use o formato AW-123456789.'
}

export function pixelTrackingReady(pixel: PixelConfigFields): boolean {
  if (pixel.provider === 'facebook') return FACEBOOK_PIXEL_ID_PATTERN.test(pixel.pixelId.trim())
  if (pixel.provider === 'tiktok') return TIKTOK_PIXEL_ID_PATTERN.test(pixel.pixelId.trim())
  if (pixel.provider === 'google_ads') {
    return (
      GOOGLE_ADS_CONVERSION_ID_PATTERN.test(pixel.pixelId.trim()) &&
      pixel.conversionLabel.trim().length > 0
    )
  }
  return pixelSecretPresent(pixel.accessToken)
}

export function pixelServerSideArmed(pixel: PixelConfigFields): boolean {
  if (pixel.provider !== 'facebook' && pixel.provider !== 'tiktok') return false
  return pixelSecretPresent(pixel.accessToken)
}
